<template>
  <div>
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card class="pa-4">
        <v-toolbar elevation="0">
          <v-card-title class="pl-0">
            {{ title }}
          </v-card-title>

          <v-spacer />

          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4 py-0">
          <v-form ref="form" v-model="formValid" lazy-validation class="pa-0 ma-0">
            <v-row>
              <v-col cols="12" md="12" sm="12" class="pt-3">
                <v-select :readonly="!canUpdateAR" v-model="formData.account_id" :error="accountError" 
                  :items="accounts.data" :loading="loadingAccounts" item-text="account_name" item-value="id" 
                  menu-props="auto" label="Conta" hide-details single-line outlined :rules="rulesAccount" color="primary">
                </v-select>
              </v-col>
              <v-col cols="12" md="12" sm="12" class="pt-0">
                <v-autocomplete multiple v-model="formData.labels" :items="labels" item-text="name" item-value="name" :loading="loadingLabels"
                  :search-input.sync="searchLabels" label="Labels" hide-details single-line outlined color="primary" dense :disabled="!formData.account_id"/>
              </v-col>
              <v-col cols="12" md="12" sm="12" class="pt-0">
                <v-select :items="numbers" v-model="formData.star_rating" label="Star rating" outlined></v-select>
              </v-col>
              <v-col cols="12" md="12" sm="12" class="pb-0">
                <v-checkbox :readonly="!canUpdateAR" v-model="formData.review_without_comment" label="Responder Sem Comentario"></v-checkbox>
              </v-col>
              <v-col cols="12" md="12" sm="12" class="pb-0">
                <v-textarea :readonly="!canUpdateAR" label="Resposta" v-model="formData.name" :rules="rules" required auto-grow outlined></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="close">
            Cancelar
          </v-btn>
          <confirm-modal v-if="actionType === 'EDIT' && canDeleteAR" :loading="loadingDelete" text action-color="error" 
            action-text="Excluir" :action="deleteKeyword" title="Excluir resposta automática" 
            description="Tem certeza que deseja excluir a resposta automática?"/>
          <v-btn v-if="canUpdateAR" :loading="loading" color="primary" text @click="save">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { handleResponseError } from '@/api/handleError'
import ConfirmModal from '@/components/ConfirmModal'
import { mapState } from 'vuex'
import * as permissionsNames from '@/constants/permissions'
import hasPermission from '@/helpers/hasPermission'
import { debounce, isObject } from 'lodash'

export default {
  name: 'AutomaticResponseDetailModal',
  props: {
    actionType: String,
    dataInfo: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    onDelete: {
      type: Function,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: Boolean
  },
  components: {
    ConfirmModal,
  },
  computed: {
    ...mapState({
      accounts: state => state.accounts.all,
      locations: state => state.locations.all,
      labels: state => {
        const labelsData = state.locations?.labels?.data || [];
        if (Array.isArray(labelsData)) {
          return labelsData.map(label => ({
            id: label.id,
            name: label.name
          }));
        } else {
          if (isObject(labelsData)) {
            return [...new Map(Object.values(labelsData).map(item => [item['name'], item])).values()]
          }
          return [];
        }
      },
    }),
    canDeleteAR() {
      return hasPermission(permissionsNames.CAN_DELETE_AUTOMATIC_RESPONSE)
    },
    canUpdateAR() {
      return hasPermission(permissionsNames.CAN_UPDATE_AUTOMATIC_RESPONSE)
    },
  },
  created() {
    this.dialog = this.value;
    this.initializeFormData();
  },
  beforeUpdate() {
    this.dialog = this.value;
  },
  methods: {
    initializeFormData() {
      this.formData.name = this.dataInfo.name;
      this.formData.account_id = this.dataInfo.account_id;
      if (Array.isArray(this.labels)) {
        this.formData.labels = this.labels.map(label => label.name);
      } else {
        this.formData.labels = [];
      }
      this.formData.star_rating = this.dataInfo.star_rating;
      this.formData.review_without_comment = this.dataInfo.review_without_comment;
      this.formDataOriginal = { ...this.data };
    },
    resetData() {
      this.initializeFormData();
      this.dialog = false;
    },
    close() {
      this.resetData()
      this.dialog = false
      this.onClose()
    },

    findAccountById(account_id) {
      if (this.accounts?.data?.length) {
        return this.accounts.data.find(account => account.id === account_id);
      }
    },

    save: async function () {
      if (this.$refs.form.validate()) {
        this.loading = true;
        if (this.actionType === 'CREATE') {
          await this.$store.dispatch('automaticResponse/create', this.formData)
            .then(() => {
              this.resetData();
              this.loading = false;
              this.onSave();
            })
            .catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao criar a resposta automática.');
              this.showNotification(message, 'red darken-2');
              this.loading = false;
            });
        } else {
          this.formData.review_without_comment = this.formData.review_without_comment ?? false;
          await this.$store.dispatch('automaticResponse/update', { id: this.dataInfo.id, data: this.formData })
            .then(() => {
              this.resetData();
              this.loading = false;
              this.onSave();
            }).catch(e => {
              const message = handleResponseError(e, 'Ocorreu um erro ao salvar a resposta automática.');
              this.showNotification(message, 'red darken-2');
              this.loading = false;
            });
        }
      }
    },
    deleteKeyword: async function () {
      this.loadingDelete = true;
      await this.$store.dispatch('automaticResponse/delete', this.dataInfo.id).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao excluir a resposta automática.');
        this.showNotification(message, 'red darken-2');
        this.loadingDelete = false;
      });
      this.loadingDelete = false;
      this.onDelete();
    },
    showNotification(message, color) {
      this.snackbar.text = message;
      this.snackbar.show = true;
      this.snackbar.color = color;
    },
    onLabelsKeyUp: debounce(function () {
      if (this.formData.account_id) {
        this.fetchLocationLabels();
      }
    }, 400),
    fetchLocationLabels: async function () {
      this.labelsError = false;
      this.loadingLabels = true;
      const account = this.findAccountById(this.formData.account_id);
      await this.$store.dispatch('locations/getLabels', {
        limit: 200,
        search: this.searchLabels,
        account: account.name,
      }).catch(e => {
          const message = handleResponseError(e, 'Ocorreu um erro ao buscar as labels.');
          this.showNotification(message, 'red darken-2');
          this.loadingLabels = false;
        });
      this.loadingLabels = false;
    },
    fetchAccounts: async function () {
      this.loadingAccounts = true;
      this.accountError = false;
      await this.$store.dispatch('accounts/getAccounts', { limit: 0 }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as contas.');
        this.showNotification(message, 'red darken-2');
        this.loadingAccounts = false;
        this.accountError = true;
      });
      this.loadingAccounts = false;
      this.accountError = false;
    },
    fetchLocations: async function () {
      if (!this.accounts) return;
      this.loadingLocations = true;
      await this.$store.dispatch('locations/getLocations', {
        account: this.accounts,
        search: this.searchLocation,
        limit: 100,
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os locais.');
        this.showNotification(message, 'red darken-2');
        this.loadingLocations = false;
      });
      this.loadingLocations = false;
    },
  },
  mounted() {
    this.fetchAccounts();
  },
  data: () => ({
    loadingAccounts: false,
    numbers: [1, 2, 3, 4, 5],
    star_rating: null,
    review_without_comment: false,
    accountError: false,
    loading: false,
    formValid: false,
    loadingDelete: false,
    searchLabels: '',
    labelsError: null,
    loadingLabels: false,
    dialog: false,
    searchLocation: '',
    loadingLocations: false,
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    formData: {
      name: '',
      account_id: null,
      labels: [],
    },
    formDataOriginal: {
      name: '',
      account_id: null,
      labels: [],
      star_rating: null,
      review_without_comment: false,
    },
    rules: [
      v => !!v || 'O nome é obrigatório',
    ],
    rulesAccount: [
      v => !!v || 'A conta é obrigatória',
    ],
  }),
  watch: {
    dialog(newVal) {
      this.$emit('input', newVal);
    },
    'dataInfo.name': function (name) {
      this.formData.name = name;
      this.formDataOriginal.name = name;
    },
    'dataInfo.account_id': function (account_id) {
      this.formData.account_id = account_id;
      this.formDataOriginal.account_id = account_id;
    },
    'dataInfo.labels': function (labels) {
      if (Array.isArray(labels)) {
        this.formData.labels = labels.map(label => label.name);
      } else if (labels && typeof(labels) === 'string') {
        this.formData.labels = labels.split(',').map(label => label.trim())
      } else {
        this.formData.labels = [];
      }
      this.formDataOriginal.labels = [...this.formData.labels];
    },
    'dataInfo.star_rating': function (star_rating) {
      this.formData.star_rating = star_rating;
      this.formDataOriginal.star_rating = star_rating;
    },
    'dataInfo.review_without_comment': function (review_without_comment) {
      let booleanData = false
      if (review_without_comment && typeof(review_without_comment) === 'string' && review_without_comment === 'Sim') {
        booleanData = true
      } else if (typeof(review_without_comment) === 'boolean' && review_without_comment) {
        booleanData = review_without_comment
      }
      this.formData.review_without_comment = booleanData;
      this.formDataOriginal.review_without_comment = review_without_comment;
    },
     'formData.account_id': function(account){
      if(account){
        this.$nextTick(() => {
          this.fetchLocationLabels();
        });
      }
     },
    'value': function(value) {
      this.dialog = value
    }
  },
}
</script>

<style scoped></style>
